import { DateFns } from '@/shared/date-fns';
import { UniAgFns } from '@/shared/uni-ag-fns';
import { GetRowIdParams, GridOptions, GridReadyEvent, RowSelectedEvent, ValueGetterParams } from '@ag-grid-community/core';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { UserAccountsService } from 'app/admin/user-accounts.service';
import {
  Account,
  InvoiceHeader,
  ItemDetail,
  ItemDetailStatusEnum,
  JoDetail,
  JoDetailAddon,
  JoHeader,
  JoHeaderPull,
  JoPullBatch,
  JoStatus,
  Manufacturer,
  PoDetail,
  ProductType,
  ReturnRequestDetail,
  ShipFrequency,
} from 'app/model/entities/entity-model';
import { ItemDetailStatusCodes } from 'app/model/enums/item-detail-status-code';
import { JobOrderMode, JobStatusCodeExt, JoStatusCode } from 'app/model/enums/jo-status-code';
import { AddressDialogComponent } from 'app/shared/address-dialog.component';

import { InvoiceDialogComponent } from '@/shared/invoice-dialog.component';
import { ItemDetailLogDialogComponent } from '@/shared/item-detail-log-dialog.component';
import { AgFns, GridOptionsExt, ISortModel } from 'app/shared/ag-fns';
import { AgStyleFns } from 'app/shared/ag-style-fns';
import { AuthUserSelectDialogComponent } from 'app/shared/auth-user-select-dialog.component';
import { AuthUser } from 'app/shared/auth.service';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { DomainBaseComponent, wrapBusy } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { EditNotesDialogComponent } from 'app/shared/edit-notes-dialog.component';
import { FileViewDialogComponent, FileViewDialogParams, IDoc } from 'app/shared/file-view-dialog.component';
import { JoUtilsService } from 'app/shared/jo-utils.service';
import { NavFns } from 'app/shared/nav-fns';
import _ from 'lodash';

import { takeUntil } from 'rxjs/operators';

enum JoTabEnum {
  Details = 0,
  Information = 1,
  Billing = 2,
  Shipping = 3,
  POs = 4,
  WIP = 5,
  Invoices = 6,
  Returns = 7,
}

@Component({
  selector: 'app-job-orders',
  templateUrl: './job-orders.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JobOrdersComponent extends DomainBaseComponent {
  @ViewChild('auditJoCell') auditJoCell: TemplateRef<any>;
  @ViewChild('invdButtonsCell') invdButtonsCell: TemplateRef<any>;
  @ViewChild('editJohNotesCell') editJohNotesCell: TemplateRef<any>;
  @ViewChild('editJodNotesCell') editJodNotesCell: TemplateRef<any>;
  // @ViewChild("addReturnRequestCell") addReturnRequestCell: TemplateRef<any>;
  @ViewChild('viewDocCell') viewDocCell: TemplateRef<any>;

  allStatuses: JoStatus[];
  currentTabIx: JoTabEnum;
  currentTabName: string; // Track tab by name to handle conditional *ngIf tabs correctly

  shipFrequencies: ShipFrequency[];

  joHeaders: JoHeader[];
  johGridOptions: GridOptionsExt;

  joDetails: JoDetail[] = [];
  jodGridOptions: GridOptionsExt;

  invoiceHeaders: InvoiceHeader[] = [];
  invhGridOptions: GridOptionsExt;

  returnRequestDetails: ReturnRequestDetail[] = [];
  rradGridOptions: GridOptionsExt;

  poDetails: PoDetail[];
  podGridOptions: GridOptionsExt;

  itemDetails: ItemDetail[] = [];
  itdGridOptions: GridOptionsExt;

  private _jobOrderMode = null;
  joPullBatch: JoPullBatch | null = null;

  currentJoHeader: JoHeader | null = null;
  inventoryProductTypeIds = new Set<number>();

  usersWithPullStaff: AuthUser[] = [];
  currentDate = new Date();
  isAccountAdmin = false;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private joUtilsService: JoUtilsService,
    private userAccountService: UserAccountsService,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    // allStatuses needs to be defined before jobOrderMode;
    this.allStatuses = this.dbQueryService.getAllCached(JoStatus);
    const joStatusAll = <JoStatus>{ id: JobOrderMode.All, name: 'All' };
    this.allStatuses.unshift(joStatusAll);

    this.shipFrequencies = this.dbQueryService.getAllCached(ShipFrequency);

    if (this.authUser.isPullAdmin) {
      this.usersWithPullStaff = await this.userAccountService.getAuthUsersWithRole('PullStaff');
    }

    this.joPullBatch = null;

    this.johGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJohGridReady,
      onRowSelected: this.onRowSelected,
      onModelUpdated: this.onJohModelUpdated,
      onFilterChanged: this.onFilterChanged,
      onFirstDataRendered: (params) => {
        this.AgFns.sizeColumnsToFit(params.api);
      },
      rowModelType: 'serverSide',
    });
    this.johGridOptions.onPaginationChanged = (params) => {
      if (params.newPage) {
        this.clearCurrentSelection();
      }
    };

    const qparams = this.captureLocation(this.johGridOptions, 'id');

    this.jobOrderMode = qparams['mode'] || JobOrderMode.All;

    this.jodGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onJodGridReady,
        onFirstDataRendered: (params) => {
          this.AgFns.autoSizeAllColumns(this.jodGridOptions);
        },
      },
      { detailProperty: 'joDetailAddons' },
    );
    this.updateJodMasterDetail(this.jodGridOptions);

    this.podGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPodGridReady,
      onFirstDataRendered: (params) => {
        this.AgFns.sizeColumnsToFit(this.podGridOptions);
      },
    });

    this.itdGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItdGridReady,
      onFirstDataRendered: (params) => {
        this.AgFns.sizeColumnsToFit(this.itdGridOptions);
      },
    });

    this.invhGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInvhGridReady,
    });

    this.rradGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onRradGridReady,
        onFirstDataRendered: (params) => {
          this.AgFns.sizeColumnsToFit(this.rradGridOptions);
        },
      },
      { detailProperty: 'creditMemos' },
    );
    this.updateRradMasterDetail(this.rradGridOptions);

    this.isAccountAdmin = this.domainService.authService.getUser().isAccountAdmin;

    this.isPageReady = true;
  }

  get canEditAddress() {
    return this.isAccountAdmin && this.currentJoHeader?.joStatusId != JoStatusCode.Closed;
  }

  override async canDeactivate() {
    this.johGridOptions?.apiX?.stopEditing();
    const ok = await super.canDeactivate();
    if (!ok) return false;

    this.uow.clearEntities(JoDetail);
    this.uow.clearEntities(JoHeader);
    this.uow.clearEntities(ProductType);
    this.uow.clearEntities(Account);
    this.uow.clearEntities(Manufacturer);
    return true;
  }

  getComparator(path: string) {
    var comparator = (valueA, valueB, nodeA, nodeB, isDescending) => {
      var a = _.get(nodeA, path);
      var b = _.get(nodeB, path);
      var r = 0;
      if (a == b) r = 0;
      if (a > b) r = 1;
      else r = -1;
      return isDescending ? r * 1 : r;
    };
    return comparator;
  }

  onJohGridReady(event: GridReadyEvent) {
    const shipFrequencies = this.shipFrequencies;
    const colDefs = [
      {
        headerName: 'J/O #',
        field: 'id',
        filter: 'agNumberColumnFilter',
        type: 'leftAligned',
        ...AgFns.fixWidth(150),
        ...AgFns.fixWidth(140),
      },

      // Ugh: can't set header name in create button props because it screws up sorting
      // but we can set it later... ???
      {
        headerName: 'Batch',
        headerClass: 'center-aligned-header',
        cellStyle: { textAlign: 'center' },
        children: [
          {
            ...AgFns.createButtonProps('', this.addEditBatch.bind(this), {
              label: 'Op',
              canDisplay: this.canAddEditBatch.bind(this),
              calcLabel: this.calcAddEditBatchLabel.bind(this),
            }),
            ...AgFns.fixWidth(90),
            suppressHeaderMenuButton: false, // need to override the default for a button
            colId: 'joPullBatchId',
            sortable: true,
            comparator: this.getComparator('joHeaderPull.joPullBatchId'),
          },
          {
            ...AgFns.createButtonProps('', this.removeFromBatch.bind(this), {
              canDisplay: this.canRemoveFromBatch.bind(this),
              label: `Remove`,
            }),
            colId: 'removeJoPullBatchId',

            ...AgFns.fixWidth(85),
          },
          {
            headerName: 'User',
            field: 'joHeaderPull.joPullBatch.userInit',
            filter: 'agSetColumnFilter',
            filterParams: {
              values: this.usersWithPullStaff.map((u) => u.initials),
              cellHeight: 25,
            },
            ...AgFns.fixWidth(100),
          },
        ],
      },
      {
        ...AgFns.createButtonProps('', this.goJoInProcess.bind(this), {
          canDisplay: this.hasWIP.bind(this),
          label: 'WIP',
          calcClass: (row) => (this.hasWIP(row) ? 'btn-has-data' : ''),
        }),
        colId: 'wip',
        ...AgFns.fixWidth(70),
      },

      {
        headerName: 'Account',
        field: 'account.accountName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },

      {
        ...AgFns.createCheckboxProps('Acct-Owned Inv Order?', 'isAccountOwnedOrder'),
        editable: false,
        ...AgFns.fixWidth(150),
      },
      {
        ...AgFns.createCheckboxProps('Pull Acct-Owned Inv?', 'allowInventory'),
        editable: false,
        ...AgFns.fixWidth(150),
      },
      {
        ...AgFns.createCheckboxProps('Charge Addons?', undefined, 'chargeAddonsOnReissuance'),
        editable: false,
        valueGetter: (p) => !!(p.data?.allowInventory && p.data?.chargeAddonsOnReissuance),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: 'Inventory Name',
        field: 'inventoryProgramName',
        filter: 'agTextColumnFilter',
        editable: false,
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createButtonProps('', this.goInvoices.bind(this), {
          label: 'Invoices/ Vouchers',
          canDisplay: this.hasInvoices.bind(this),
          calcLabel: this.calcInvoicesLabel.bind(this),
        }),
        colId: 'invoices',
        suppressHeaderMenuButton: true,
        ...AgFns.fixWidth(100),
      },
      { headerName: 'Status', field: 'joStatus.name', ...AgFns.fixWidth(200) },
      { headerName: 'J/O Date', field: 'joDate', filter: 'agDateColumnFilter', ...AgFns.fixWidth(150) },
      {
        headerName: 'Exp. Ship Date',
        field: 'origExpectedShipDate',
        filter: 'agDateColumnFilter',
        cellClass: (params: any) => AgStyleFns.getDueDateCellClass(params.value),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: 'Ship Frequency',
        editable: this.isShipFrequencyEditable.bind(this),
        cellClass: this.getShipFrequencyCellClass.bind(this),
        ...AgFns.fixWidth(180),
        sortable: false,
        ...AgFns.createDropdownEditor('shipFrequencyId', this.shipFrequencies, this.saveShipFrequencyIfChanged.bind(this)),
      },
      // {
      //   ...AgFns.createCheckboxProps("Account pays freight", "doesAccountPayFreight"),
      //   editable: false,
      //   ...AgFns.fixWidth(100),
      // },
      // {
      //   ...AgFns.createCheckboxProps("Allow Partial Shipments", "allowPartialShipments"),
      //   editable: false,
      //   ...AgFns.fixWidth(100),
      // },

      // {
      //   headerName: "Manifest",
      //   ...NavFns.createIdCellClickedNavProps("manifestId", this.router, "/manifests"),
      //   ...AgFns.fixWidth(80),
      // },

      { ...AgFns.createCellButtonProps('Notes', this.editJohNotesCell), ...AgFns.fixWidth(80) },

      // {
      //   ...AgFns.createButtonProps("Return Request", this.addEditReturnRequest.bind(this), {
      //     canDisplay: this.canAddEditReturnRequest.bind(this),
      //     label: `Add/Edit`,
      //   }),
      //   ...AgFns.fixWidth(100),
      // },
      {
        ...AgFns.createCellButtonProps('Audit', this.auditJoCell),
        ...AgFns.fixWidth(90),
      },
      // { headerName: 'Handling Charge/Order', field: 'handlingChargePerOrderAmt', filter: 'agNumberColumnFilter' },
      // { headerName: 'Handling Charge/Shipment', field: 'handlingChargePerShipmentAmt', filter: 'agNumberColumnFilter' },
    ];

    const sortModel = this.getJohSortModel();

    AgFns.initGrid(event, colDefs, sortModel);

    this.updateJoColumnVisibility();
    this.updateDatasource(true);
    this.applyLocation();
  }

  private getJohSortModel() {
    let sortModel: ISortModel;
    if (this.jobOrderMode === JobOrderMode.All) {
      sortModel = [{ colId: 'id', sort: 'desc' }];
    } else {
      sortModel = [
        { colId: 'joHeaderPull.joPullBatchId', sort: 'desc' },
        { colId: 'id', sort: 'desc' },
      ];
    }
    return sortModel;
  }

  updateJoColumnVisibility() {
    const gridApi = this.johGridOptions?.apiX;
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }

    gridApi.setColumnsVisible(['joPullBatchId'], this.jobOrderMode == JobOrderMode.OpenProcessIncomplete);
    gridApi.setColumnsVisible(['removeJoPullBatchId'], this.jobOrderMode == JobOrderMode.OpenProcessIncomplete);
    gridApi.setColumnsVisible(['joHeaderPull.joPullBatch.userInit'], this.jobOrderMode == JobOrderMode.OpenProcessIncomplete);
    gridApi.setColumnsVisible(['wip'], this.jobOrderMode == JobOrderMode.OpenProcessComplete);
    gridApi.setColumnsVisible(['joStatus.name'], this.jobOrderMode === JobOrderMode.All);
    gridApi.setColumnsVisible(
      ['invoices'],
      this.jobOrderMode === JobOrderMode.Closed,
    );
    gridApi.setColumnsVisible(
      ['addReturnRequest'],
      this.jobOrderMode == JobOrderMode.OpenProcessComplete || this.jobOrderMode === JobOrderMode.Closed,
    );

    if (this.jobOrderMode == JobOrderMode.OpenProcessIncomplete) {
      gridApi.moveColumns(['joPullBatchId'], 1);
    }
    // gridApi.autoSizeAllCviolumns();
    // AgFns.sizeColumnsToFit(gridApi);
  }

  // Ship Frequency related methods - for testing only

  private isShipFrequencyEditable(p: any) {
    if (!this.user.isAccountAdmin) return false;
    const joh = p.data as JoHeader;
    if (!joh) return false;
    if (joh.joStatusId == JoStatusCode.Closed) return false;
    return true;
  }

  private getShipFrequencyCellClass(p: any) {
    if (this.isShipFrequencyEditable(p)) {
      return AgStyleFns.getEditableCellClass();
    } else {
      return '';
    }
  }

  private async saveShipFrequencyIfChanged(p: any) {
    const joh = p.data as JoHeader;
    if (joh && joh.entityAspect.entityState.isModified()) {
      await this.dbSaveService.saveSelectedChanges([joh]);
    }
  }

  // -------------------------------------------

  onJodGridReady(event: GridReadyEvent) {
    const standardMaxWidth = 130;
    const colDefs = [
      // {
      //   headerName: "J/O Detail Id",
      //   field: "id",
      //   cellRenderer: "agGroupCellRenderer",
      // },
      {
        ...AgFns.createButtonProps('', this.noop.bind(this), {
          icon: 'pi pi-info-circle',
          canDisplay: (jod: JoDetail) => this.isInInventoryProgram(jod),
          label: '',
          tooltip: 'Available in account inventory program',
          colId: 'inventoryIndicator',
        }),
        headerName: '',
        headerTooltip: 'Item available in account inventory program',
        sortable: false,
        ...AgFns.fixWidth(45),
      },
      {
        ...UniAgFns.getManufacturerColDef('product.productType.manufacturer.name'),
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      { ...UniAgFns.getProductTypeSKUColDef('product.productType.style'), ...AgFns.flexWidth(100) },
      { headerName: 'Config', field: 'productTypeConfigName', ...AgFns.flexWidth(100) },
      { ...UniAgFns.getProductTypeDescriptionColDef('product.productType.description'), ...AgFns.flexWidth(200) },
      { ...UniAgFns.getFeatureChoicesColDef('product.featureChoicesExtract'), ...AgFns.flexWidth(100) },
      {
        headerName: 'Exp. Ship Date',
        field: 'calcExpectedShipDate',
        ...AgFns.fixWidth(160),
        valueFormatter: (params: any) => DateFns.fmtDate(params.value),
        cellClass: (params: any) => AgStyleFns.getDueDateCellClass(params.value),
      },
      {
        ...AgFns.createCellButtonProps('Notes', this.editJodNotesCell),
        ...AgFns.fixWidth(90),
      },
      {
        headerName: 'Quantities',
        children: [
          {
            headerName: 'Order',
            field: 'orderQty',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
          },
          {
            headerName: 'Cancel',
            field: 'cancelQty',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
          },
          // could break this into pulled vs rcvd
          {
            colId: 'pullQty',
            headerName: 'Pull/Rcvd',
            type: 'numericColumn',
            headerTooltip: '(calc) Quantities already pulled or received',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getItemDetailsRcvdQty();
            },
            ...AgFns.fixWidth(120),
          },
          {
            colId: 'openPoQty',
            headerName: 'Open P/O',
            type: 'numericColumn',
            headerTooltip: '(calc) P/O Detail quantities not yet received or canceled',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getPoDetailsRemainingQty();
            },
            ...AgFns.fixWidth(120),
          },
          {
            colId: 'heldPoQty',
            headerName: 'Held P/O',
            type: 'numericColumn',
            headerTooltip: '(calc) P/O Detail quantities held',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getPoDetailsHeldQty();
            },
            ...AgFns.fixWidth(120),
          },
          {
            colId: 'returnQty',
            headerName: 'Return',
            field: 'returnQty',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
          },
          {
            colId: 'shippedAndVoucheredQty',
            headerName: "Vouchr/Ship'd",
            type: 'numericColumn',
            headerTooltip: '(calc) Number of items vouchered/shipped for this order detail',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getShippedAndVoucheredQty();
            },
            ...AgFns.fixWidth(145),
          },
          {
            colId: 'damagedQty',
            headerName: 'Damaged',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getItemDetailsDamagedQty();
            },
            headerTooltip: '(calc) Quantities damaged/lost/other',
            type: 'numericColumn',
            ...AgFns.fixWidth(120),
          },
          {
            colId: 'openQty',
            headerName: 'Open',
            type: 'numericColumn',
            headerTooltip: '(calc) The net quantities remaining after accounting for pulled items and unreceived purchase order details',
            valueGetter: (params: any) => {
              const jod = <JoDetail>params.data;
              return jod.getUnshippedAndUnvoucheredQty();
            },
            ...AgFns.fixWidth(90),
          },
          // {
          //   headerName: 'Remaining Unresolved',
          //   type: 'numericColumn',
          //   headerTooltip: '(calc) The remaining unresolved quantity',
          //   valueGetter: (params: any) => {
          //     const jod = <JoDetail>params.data;
          //     return jod.getUnresolvedQty();
          //   },
          //   ...AgFns.fixWidth(140),
          // },
        ],
      },
    ];
    const sortFields = ['product.productType.manufacturer.name', 'product.productType.description', 'product.productType.style'];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: 'asc' };
    }) as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateJodColumnVisibility();
  }

  updateJodMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: 'Addon', field: 'addon.nameAndLocation' },
      { headerName: 'Addl. Info', field: 'additionalInfo' },
      { headerName: 'Price/Unit', field: 'unitPriceAmt' },
      { ...AgFns.createCellButtonProps('Document', this.viewDocCell) },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);

    ((detailGridOptions.getRowId = (params: GetRowIdParams) => {
      const jod = params.data as JoDetail;
      return jod.id.toString();
    }),
      (parentGridOptions.detailCellRendererParams = {
        detailGridOptions: detailGridOptions,
        refreshStrategy: 'everything',

        getDetailRowData: (params: any) => {
          const jod = params.data as JoDetail;
          // load docs when addons are expanded
          const jodAddonIds = jod.joDetailAddons.map((joda) => joda.id);
          this.dbQueryService.getJodAddonsWithDocs(jodAddonIds).then(() => {
            params.successCallback(jod.joDetailAddons);
          });
        },
      }));
  }

  onPodGridReady(event: GridReadyEvent) {
    const colDefs = [
      // Already shown above on PurchaseOrder
      {
        headerName: 'Purchase Order',
        ...NavFns.createIdCellClickedNavProps('poHeaderId', this.router, '/purchasing/manage/purchase-orders'),
        maxWidth: 160,
        cellClass: 'cell-link',
        filter: 'agNumberColumnFilter',
        type: 'leftAligned',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Status',
        field: 'poHeader.poStatus.name',
        cellStyle: (params) => {
          if (params.data.poHeader.poStatusId == '1000.00') {
            return { 'background-color': '#ffcccc' };
          } else {
            return null;
          }
        },
        ...AgFns.flexWidth(120),
      },

      { ...UniAgFns.getManufacturerColDef('product.productType.manufacturer.name') },
      { ...UniAgFns.getProductTypeSKUColDef('product.productType.style') },
      { ...UniAgFns.getProductTypeDescriptionColDef('product.productType.description') },
      { ...UniAgFns.getFeatureChoicesColDef('product.featureChoicesExtract') },

      {
        headerName: 'Exp. Receive Date',
        colId: 'joDetail.expectedReceiveDate', // needs to end with date for formatter to work.
        valueGetter: (params) => {
          if (params.data.getRemainingQty() > 0) {
            return params.data.expectedReceiveDate;
          } else {
            return null;
          }
        },
        cellClass: (params: any) => {
          const pod = params.data as PoDetail;
          return AgStyleFns.getPodDueDateCellClass(pod.poHeader.isProcessed(), pod.getRemainingQty() > 0, params.value);
        },
        // ...AgFns.fixWidth("date"),
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Exp. Ship Date',
        field: 'joDetail.expectedShipDate',
        valueGetter: (params: any) => {
          if (params.data.getRemainingQty() > 0) {
            return params.data.expectedShipDate;
          } else {
            return null;
          }
        },
        valueFormatter: (params: any) => DateFns.fmtDate(params.value),
        cellClass: (params: any) => {
          const pod = params.data as PoDetail;
          return AgStyleFns.getPodDueDateCellClass(pod.poHeader.isProcessed(), pod.getRemainingQty() > 0, params.value);
        },
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Quantities',
        headerClass: 'center-aligned-header',
        cellStyle: { textAlign: 'center' },
        children: [
          {
            headerName: 'Order',
            field: 'orderQty',
            ...AgFns.fixWidth(100),
            type: 'numericColumn',
          },
          {
            headerName: 'Held',
            field: 'heldQty',
            ...AgFns.fixWidth(100),
            type: 'numericColumn',
          },
          {
            headerName: 'Rcvd',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
            valueGetter: (params: any) => {
              const pod = <PoDetail>params.data;
              return pod?.getReceivedQty();
            },
          },
          {
            headerName: 'Dmgd',
            headerTooltip: '(calc) Of those received, how many are damaged, lost or other',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
            valueGetter: (params: any) => {
              const pod = <PoDetail>params.data;
              return pod?.getDamagedQty();
            },
          },
          {
            headerName: 'Cncl',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getCanceledQty();
            },
          },
          {
            headerName: 'Open',
            type: 'numericColumn',
            ...AgFns.fixWidth(100),
            valueGetter: (params: any) => {
              const pod = <PoDetail>params.data;
              return pod?.getRemainingQty();
            },
          },
          {
            headerName: 'Shipped',
            type: 'numericColumn',
            ...AgFns.fixWidth(120),
            valueGetter: (params: any) => {
              const pod = <PoDetail>params.data;
              return pod?.getShippedQty();
            },
          },
        ],
      },
      // { headerName: "P/O Detail", field: "id" },
      // { headerName: "J/O Detail", field: "joDetailId" },
    ];
    const sortFields = ['product.productType.description', 'product.productType.style', 'product.choiceValue', 'poDetailTypeId'];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: 'asc' as const };
    });

    this.podGridOptions.getRowClass = function (params: any) {
      const pod = params.node.data as PoDetail;
      if (pod.getRemainingQty() == 0) {
        return 'rs-completed';
      }
    };

    AgFns.initGrid(event, colDefs, sortModel);
    this.updatePodColumnVisibility();
  }

  onItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef('product.productType.manufacturer.name') },
      { ...UniAgFns.getProductTypeSKUColDef('product.productType.style') },
      { ...UniAgFns.getProductTypeDescriptionColDef('product.productType.description') },
      { ...UniAgFns.getFeatureChoicesColDef('product.featureChoicesExtract') },

      { headerName: 'Status', field: 'itemDetailStatus.name', ...AgFns.fixWidth(250) },
      {
        ...AgFns.createCheckboxProps('Account-Owned', 'isAccountOwned'),
        editable: false,
        ...AgFns.fixWidth(160),
      },
      {
        ...AgFns.createButtonProps('', this.gotoProcess.bind(this), {
          label: 'In Process',
          calcLabel: this.getProcessLabel.bind(this),
        }),
        colId: 'inProcess',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Inventory Identifier',
        field: 'id',
        cellClass: AgStyleFns.getLinkCellClass() + ' ag-left-aligned-cell',
        onCellClicked: (e: any) => this.openItemStory(e.data as ItemDetail),
        tooltipValueGetter: () => "Show this item's story",
        ...AgFns.fixWidth(160),
      },
    ];

    AgFns.initGrid(event, colDefs);
  }

  async openItemStory(itd: ItemDetail) {
    if (!itd) return;
    await DialogBaseComponent.open(ItemDetailLogDialogComponent, this, { itemDetail: itd });
  }

  onInvhGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps('Invoice', this.openInvoiceDialog.bind(this), {
          calcLabel: (invh: InvoiceHeader) => invh.id,
        }),
        field: 'id',
        ...AgFns.fixWidth(120),
      },
      { headerName: 'Date', field: 'invoiceDate', ...AgFns.flexWidth(200) },
      { headerName: 'Status', field: 'invoiceStatus.name', ...AgFns.flexWidth(200) },
      {
        headerName: 'Amounts',
        children: [
          { headerName: 'Subtotal', field: 'subtotalAmt', ...AgFns.flexWidth(200) },
          { headerName: 'Tax', field: 'taxAmt', ...AgFns.flexWidth(200) },
          { headerName: 'Freight', field: 'freightAmt', ...AgFns.flexWidth(200) },
          { headerName: 'Handling', field: 'orderHandlingAmt', ...AgFns.flexWidth(200) },
          { headerName: 'Prepayment', field: 'prepaymentAmt', ...AgFns.flexWidth(200) },
          {
            headerName: 'Total',
            colId: 'totalAmt',
            valueGetter: (p: ValueGetterParams<InvoiceHeader>) => p.data?.roundedTotalAmt(),
            ...AgFns.flexWidth(200),
          },
        ],
      },
      { headerName: 'Due Date', field: 'dueDate', ...AgFns.fixWidth(150) },
      { headerName: 'GL Invoice ID', field: 'gLInvoiceId', ...AgFns.flexWidth(120) },
    ];

    const sortModel = [{ colId: 'id', sort: 'desc' as const }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  onRradGridReady(event: any) {
    const colDefs = [
      {
        headerName: 'Manufacturer',
        field: 'joDetail.product.productType.manufacturer.name',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(160),
      },
      { headerName: 'SKU', field: 'joDetail.product.productType.style', ...AgFns.flexWidth(100) },
      {
        headerName: 'Description',
        field: 'joDetail.product.productType.description',
        ...AgFns.flexWidth(300),
      },
      {
        headerName: 'Features',
        field: 'joDetail.product.productfeatureChoicesExtract',
        ...AgFns.flexWidth(100),
      },
      { headerName: 'Action', field: 'returnRequestDetailAction.name', ...AgFns.fixWidth(140) },
      { headerName: 'Status', field: 'returnRequestDetailStatus.name', ...AgFns.fixWidth(140) },
      { headerName: 'Qty', field: 'qty', ...AgFns.fixWidth(90) },
      {
        headerName: 'Notes',
        field: 'notes',
        cellEditor: 'agLargeTextCellEditor',
        ...AgFns.flexWidth(200),
      },
      // { headerName: "J/O Detail Id", field: "joDetail.id" },
    ];

    const sortModel = [{ colId: 'returnRequestDetailAction.name', sort: 'desc' as const }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  updateRradMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();

    detailGridOptions.columnDefs = [
      { headerName: 'Credit Memo Amount', field: 'creditMemoAmt' },
      { headerName: 'Status', field: 'creditMemoStatus.name' },
      { headerName: 'Notes', field: 'notes' },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const rrad = params.data as ReturnRequestDetail;
        params.successCallback(rrad.creditMemos);
      },
    };
  }

  get jobOrderMode() {
    return this._jobOrderMode;
  }
  set jobOrderMode(mode: JobStatusCodeExt) {
    if (this.jobOrderMode !== mode) {
      this.updateJobOrderMode(mode);
    }
  }

  @wrapBusy
  private async updateJobOrderMode(mode: JobStatusCodeExt) {
    this._jobOrderMode = mode;
    const gridApi = this.johGridOptions?.apiX;
    if (gridApi) {
      gridApi.setGridOption('serverSideDatasource', null);
      gridApi.setFilterModel(null);
      this.johGridOptions!.apiX!.applyColumnState({
        state: this.getJohSortModel(),
      });
      this.clearCurrentSelection();
      // need to make sure that we return to the first page after the updateDatasource call below.
      // but we don't want to do it on the original set of jobOrderMode
      const gridState = this.johGridOptions.context?.gridState;
      if (gridState != null) {
        gridState.pg = 1;
      }
    }

    this.updateJoColumnVisibility();
    this.updateJodColumnVisibility();
    this.updatePodColumnVisibility();
    this.updateDatasource(true);
    this.replaceLocation();
  }

  get currentJoStatus() {
    return this.allStatuses.find((s) => s.id === this.jobOrderMode);
  }
  set currentJoStatus(value: JoStatus) {
    if (value && this.currentJoStatus !== value) {
      this.jobOrderMode = <JobStatusCodeExt>value.id;
    }
  }

  onFilterChanged() {
    this.clearCurrentSelection();
  }

  clearCurrentSelection() {
    // insure that bound vars are cleared after any sorts or filters. - because selection is lost

    // HACK:
    // this insures that we don't reenter here after the deselectAll call below which causes a modelUpdated event
    // which calls this.  The problem is when returning to the page this method is called before the 'key' row is selected
    // but then is called again after the row is selected ( because deselectAll causes modelUpdated after a timeout) and this
    // 2nd call clears the row that has just been selected. UGH.
    if (this.currentJoHeader === null) {
      return;
    }
    this.currentJoHeader = null;

    this.joDetails = [];
    this.invoiceHeaders = [];
    this.returnRequestDetails = [];
    this.poDetails = [];
    this.itemDetails = [];

    this.johGridOptions.apiX?.deselectAll();
  }

  isModeIncomplete() {
    return this.jobOrderMode === JobOrderMode.OpenProcessIncomplete;
  }

  isModeComplete() {
    return this.jobOrderMode === JobOrderMode.OpenProcessComplete;
  }

  isModeAll() {
    return this.jobOrderMode === JobOrderMode.All;
  }

  async updateDatasource(sizeToFit = false) {
    const gridApi = this.johGridOptions && this.johGridOptions.apiX;
    if (gridApi == null) return;

    const opts = sizeToFit ? { returnFn: () => AgFns.sizeColumnsToFit(this.johGridOptions) } : undefined;
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createJoHeaderModeQuery(this.jobOrderMode), undefined, opts);
    gridApi.setGridOption('serverSideDatasource', ds);
  }

  replaceLocation(key: any = null) {
    const otherQueryParams = {
      mode: this.jobOrderMode,
    };
    super.replaceLocation(key, otherQueryParams);
  }

  updateJodColumnVisibility() {
    const gridApiJod = this.jodGridOptions?.apiX;
    if (!gridApiJod || gridApiJod.isDestroyed()) {
      return;
    }

    gridApiJod.setColumnsVisible(
      ['calcExpectedShipDate'],
      this.jobOrderMode === JobOrderMode.OpenProcessComplete || this.jobOrderMode === JobOrderMode.All,
    );
  }

  updatePodColumnVisibility() {
    const gridApiPod = this.podGridOptions?.apiX;
    if (!gridApiPod || gridApiPod.isDestroyed()) {
      return;
    }

    gridApiPod.setColumnsVisible(
      ['joDetail.calcExpectedShipDate'],
      this.jobOrderMode === JobOrderMode.OpenProcessComplete || this.jobOrderMode === JobOrderMode.All,
    );

    gridApiPod.setColumnsVisible(
      ['calcExpectedReceiveDate'],
      this.jobOrderMode === JobOrderMode.OpenProcessComplete || this.jobOrderMode === JobOrderMode.All,
    );
  }

  getTitle() {
    if (this.jobOrderMode === JobOrderMode.OpenProcessIncomplete) {
      const batchId = this.joPullBatchId ?? ' [New] ';
      const batchUser = this.joPullBatch ? ' - For Pull Staff: ' + this.joPullBatch.userInit : '';
      const batchText = `Current Batch: ${batchId}  ${batchUser}`;
      return ` - Open/Process Incomplete Status - ` + batchText;
    } else if (this.jobOrderMode === JobOrderMode.OpenProcessComplete) {
      return ` - Open/Process Complete Status`;
    } else if (this.jobOrderMode === JobOrderMode.Closed) {
      return ` - Closed Status`;
    } else {
      return ' - All Statuses';
    }
  }

  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const joh = e.data as JoHeader;
    if (!joh) {
      return;
    }
    this.currentJoHeader = joh;

    this.replaceLocation(joh.id);

    if (this.jobOrderMode === JobOrderMode.OpenProcessIncomplete) {
      if (joh.joHeaderPull != null) {
        if (joh.joHeaderPull.joPullBatch.canUserEdit(this.authUser)) {
          this.joPullBatch = joh.joHeaderPull.joPullBatch;
        }
      }
    }
    const promises = [this.queryJoDetails(joh)] as any[];
    // JM: 12/29/2025 - Use currentTabName instead of currentTabIx to handle conditional *ngIf tabs correctly
    // OLD CODE (broken when tabs are conditionally hidden):
    // if (this.currentTabIx === JoTabEnum.Invoices) { ... }
    if (this.currentTabName === 'Invoices') {
      promises.push(this.queryInvoices(joh));
    } else if (this.currentTabName === 'Returns') {
      promises.push(this.queryReturnRequestDetails(joh));
    } else if (this.currentTabName === 'WIP - Pulled/Received') {
      promises.push(this.queryItemDetails(joh));
    }

    await Promise.all(promises);
    this.poDetails = _.flatMap(this.joDetails.map((jod) => jod.poDetails));
  }
  async queryJoDetails(joh: JoHeader) {
    // Can't use breeze.isNavigationLoaded here because we need to check for deeper
    // navigation than just joDetails.

    if (!(joh as any)['areJoDetailsLoaded']) {
      await AgFns.busyGrid(this.jodGridOptions, this, async () => {
        this.joDetails = await this.dbQueryService.getJoDetails(joh.id);
      });
      (joh as any)['areJoDetailsLoaded'] = true;
    } else {
      this.joDetails = joh.joDetails;
    }
    this.poDetails = _.flatMap(this.joDetails.map((jod) => jod.poDetails));

    // Load inventory program productTypeIds for the icon indicator
    if (joh.allowInventory && joh.inventoryProgramName) {
      const ids = await this.dbQueryService.getAccountOwnedProductTypeIds(joh.inventoryProgramName);
      this.inventoryProductTypeIds = new Set(ids);
    } else {
      this.inventoryProductTypeIds = new Set();
    }

    return this.joDetails;
  }

  isInInventoryProgram(jod: JoDetail): boolean {
    return this.inventoryProductTypeIds.has(jod.product?.productTypeId);
  }

  noop() {}

  async queryInvoices(joh: JoHeader) {
    if (!(joh as any)['areInvoiceHeadersLoaded']) {
      await AgFns.busyGrid(this.invhGridOptions, this, async () => {
        this.invoiceHeaders = await this.dbQueryService.getInvoiceHeadersByJoHeaderId(joh.id);
      });
      (joh as any)['areInvoiceHeadersLoaded'] = true;
    } else {
      this.invoiceHeaders = joh.invoiceHeaders.slice(0);
    }
    return this.invoiceHeaders;
  }

  async queryReturnRequestDetails(joh: JoHeader) {
    if (!(joh as any)['areReturnsLoaded']) {
      await AgFns.busyGrid(this.rradGridOptions, this, async () => {
        const returnRequests = await this.dbQueryService.getReturnRequestsByJoHeaderId(joh.id);
      });
      (joh as any)['areReturnsLoaded'] = true;
    }

    this.returnRequestDetails = _.flatten(joh.returnRequests.map((rra) => rra.returnRequestDetails));
    return this.returnRequestDetails;
  }

  async queryItemDetails(joh: JoHeader) {
    if (!(joh as any)['areItemDetailsLoaded']) {
      await AgFns.busyGrid(this.itdGridOptions, this, async () => {
        const allItemDetails = await this.dbQueryService.getItemDetailsByJoHeaderId(joh.id);
        // Filter for WIP items (pulled/received but not yet shipped)
        this.itemDetails = allItemDetails.filter(
          (itd) => itd.itemDetailStatusId < ItemDetailStatusEnum.Shipped && itd.itemDetailStatusId != 0,
        );
      });
      (joh as any)['areItemDetailsLoaded'] = true;
    } else {
      // Re-filter from cached data
      const allItemDetails = joh.joDetails.flatMap((jod) => jod.itemDetails);
      this.itemDetails = allItemDetails.filter(
        (itd) => itd.itemDetailStatusId < ItemDetailStatusEnum.Shipped && itd.itemDetailStatusId != 0,
      );
    }
    return this.itemDetails;
  }

  gotoProcess(itd: ItemDetail) {
    if (ItemDetailStatusCodes.Addon.includes(itd.itemDetailStatusId)) {
      this.router.navigate(['/in-process/manage/addon-bin', itd.itemDetailAddons[0].addonBinId]);
    } else if (ItemDetailStatusCodes.ShipPrep.includes(itd.itemDetailStatusId)) {
      const params = NavFns.buildNavIdParams(itd.joDetail.joHeaderId, 'joDetail.joHeader.id');
      this.router.navigate(['/in-process/manage/scan-station'], params);
    } else if (itd.invoiceHeaderId != null) {
      this.openInvoiceDialogById(itd.invoiceHeaderId);
    }
  }

  async openInvoiceDialog(invh: InvoiceHeader) {
    await InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, {
      invoiceId: invh.id,
    });
  }

  async openInvoiceDialogById(invoiceId: string) {
    await InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, {
      invoiceId: invoiceId,
    });
  }

  getProcessLabel(itd: ItemDetail) {
    if (ItemDetailStatusCodes.Addon.includes(itd.itemDetailStatusId)) {
      return 'AddonBin-' + itd.itemDetailAddons[0].addonBinId;
    } else if (ItemDetailStatusCodes.ShipPrep.includes(itd.itemDetailStatusId)) {
      return 'Ship Prep';
    } else if (ItemDetailStatusEnum.InProcessVoucher == itd.itemDetailStatusId) {
      return 'Invoice';
    } else {
      return '';
    }
  }

  get joPullBatchId() {
    return this.joPullBatch ? this.joPullBatch.id : null;
  }

  onJohModelUpdated(params) {
    this.clearCurrentSelection();
  }

  async editAddress(prefix: string, addressTypeName: string) {
    const r = await DialogBaseComponent.open(AddressDialogComponent, this, {
      entity: this.currentJoHeader as any,
      addressPrefix: prefix,
      addressTypeName: addressTypeName,
      editable: true,
    });
  }

  hasJohNotes(joh: JoHeader) {
    return joh && joh.joNotes.length > 0;
  }

  async editJohNotes(joh: JoHeader) {
    await DialogBaseComponent.open(EditNotesDialogComponent, this, {
      xHeader: joh,
    });
  }

  hasJodNotes(jod: JoDetail) {
    return jod && jod.joNotes.length > 0;
  }

  async editJodNotes(jod: JoDetail) {
    await DialogBaseComponent.open(EditNotesDialogComponent, this, {
      xDetail: jod,
    });
  }

  goJoAudit(joh: JoHeader) {
    if (this.updateCurrentJoh(joh)) {
      const params = NavFns.buildNavIdParams(joh.id);
      this.router.navigate(['/in-process/manage/jo-audit'], params);
    }
  }

  goPrintableInvoice(invh: InvoiceHeader) {
    if (invh != null) {
      this.router.navigate(['/printable-invoice', invh.id]);
    }
  }

  async createNewBatch() {
    this.joPullBatch = null;
    this.toastr.success('New batch created');
  }

  canAddBatch(joh: JoHeader) {
    return this.authUser.isPullAdmin && joh != null && joh.joStatusId === JoStatusCode.OpenProcessIncomplete;
  }

  canEditBatch(joh: JoHeader) {
    return this.authUser.isPullStaff && joh != null && joh.joHeaderPull != null && joh.joHeaderPull.joPullBatch.canUserEdit(this.authUser);
  }

  canRemoveFromBatch(joh: JoHeader) {
    return this.authUser.isPullAdmin && joh != null && joh.joHeaderPull != null && joh.joStatusId === JoStatusCode.OpenProcessIncomplete;
  }

  canAddEditBatch(joh: JoHeader) {
    if (joh.isAccountOwnedOrder) {
      return joh.joStatusId === JoStatusCode.OpenProcessIncomplete;
    }
    if (joh.joHeaderPull == null) {
      if (this.canAddBatch(joh)) {
        return true;
      }
    } else {
      if (this.canEditBatch(joh)) {
        return true;
      }
    }
    return false;
  }

  calcAddEditBatchLabel(joh: JoHeader) {
    if (joh.isAccountOwnedOrder) {
      return joh.joStatusId === JoStatusCode.OpenProcessIncomplete ? 'Order' : '';
    }
    if (joh.joHeaderPull == null) {
      return this.canAddBatch(joh) ? 'Add' : '';
    } else {
      return this.canEditBatch(joh) ? `Edit - ${joh?.joHeaderPull?.joPullBatchId}` : '';
    }
  }

  async addEditBatch(joh: JoHeader) {
    await this.queryJoDetails(joh);
    if (joh.joDetails.length === 0) {
      const ok = await this.dialogService.yesNoDialog('Delete Job Order', `Job Order ${joh.id} has no details. Should it be deleted?`);
      if (ok) {
        joh.entityAspect.setDeleted();
        await this.dbSaveService.saveChanges();
        this.toastr.success(`Job Order ${joh.id} deleted`);
        this.updateDatasource();
      }
      return;
    }

    if (joh.isAccountOwnedOrder) {
      return await this.processAccountOwnedOrder(joh);
    }

    if (joh.joHeaderPull == null) {
      return await this.addToBatch(joh);
    } else {
      return await this.editBatch(joh);
    }
  }

  @wrapBusy
  async processAccountOwnedOrder(joh: JoHeader) {
    const ok = await this.dialogService.yesNoDialog(
      'Process Account-Owned Order',
      `Create purchase orders for Job Order ${joh.id} and mark as complete?`,
    );
    if (!ok) return;

    const odCount = await this.joUtilsService.createPurchaseOrdersForJoDetails(joh.joDetails);
    joh.joStatusId = JoStatusCode.OpenProcessComplete;
    await this.dbSaveService.saveChanges();

    this.johGridOptions.apiX?.refreshCells();
    this.toastr.success(`Order ${joh.id}: created ${odCount} purchase order detail(s)`);
  }

  @wrapBusy
  async addToBatch(joh: JoHeader) {
    // check if status is ok
    // TODO: create EnumGenerator for lookup tables.
    if (joh.joStatusId !== JoStatusCode.OpenProcessIncomplete) {
      this.toastr.warning('Only Job Orders with a Status of: Open/Process Incomplete may be added to a batch');

      return false;
    }

    await this.queryJoDetails(joh);
    if (joh.joDetails.length === 0) {
      this.toastr.warning('Cannot add this Job Order because it has no details');
      return false;
    }

    let joHeaderPull = joh.joHeaderPull;
    // check if joHeader is already part of another group

    if (joHeaderPull) {
      if (this.joPullBatchId && joHeaderPull.joPullBatchId === this.joPullBatchId) {
        // just adding it again - ignore.
        return true;
      }
    }

    if (this.joPullBatchId == null) {
      const aus = await DialogBaseComponent.open(AuthUserSelectDialogComponent, this, {
        authUsers: this.usersWithPullStaff,
      });
      if (aus.length != 1) {
        return false;
      }
      const pullStaffAu = aus[0];
      this.joPullBatch = this.uow.createEntity(JoPullBatch, {
        userInit: pullStaffAu.initials,
        modTs: new Date(),
      });
    } else {
      this.joPullBatch.modTs = new Date();
    }

    joHeaderPull = this.uow.createEntity(JoHeaderPull, {
      joHeaderId: joh.id,
      joPullBatchId: this.joPullBatchId,
    });

    await this.dbSaveService.saveChanges();
    // next line is needed to insure that joHeader.joPullBatchId column is refreshed.
    this.johGridOptions.apiX?.refreshCells();
    return true;
  }

  @wrapBusy
  async editBatch(joh: JoHeader) {
    this.updateCurrentJoh(joh);
    const joPullBatchId = joh?.joHeaderPull?.joPullBatchId;
    if (joPullBatchId != null) {
      this.router.navigate(['/in-process/manage/jo-pull-to-wip', joPullBatchId]);
    }
  }

  @wrapBusy
  async removeFromBatch(joh: JoHeader) {
    const ok = await this.dialogService.yesNoDialog('Remove this Job Order from its batch', 'Are you sure?');
    if (!ok) {
      return;
    }

    this.toastr.info('Removing Job Order from Pull batch');
    await this.joUtilsService.deleteJoHeaderFromBatch(joh.id);
    this.toastr.success('Job Order removal complete');
    // next line is needed to insure that joHeader.joPullBatchId column is refreshed.
    this.joPullBatch = null;
    this.johGridOptions.apiX?.refreshCells();
  }

  canViewInvoices(joh: JoHeader) {
    return this.hasInvoices(joh) && this.areAllInvoicesPosted(joh);
  }

  canEditInvoices(joh: JoHeader) {
    return this.hasInvoices(joh) && !this.areAllInvoicesPosted(joh);
  }

  calcInvoicesLabel(joh: JoHeader) {
    if (!this.hasInvoices(joh)) {
      return '';
    }
    return this.areAllInvoicesPosted(joh) ? 'View' : 'Edit';
  }

  hasInvoices(joh: JoHeader) {
    return joh != null && joh.invoiceHeaders != null && joh.invoiceHeaders.length > 0;
  }

  areAllInvoicesPosted(joh: JoHeader) {
    return joh != null && joh.invoiceHeaders != null && joh.invoiceHeaders.every((invh) => invh.isPostedOrQueued);
  }

  async goInvoices(joh: JoHeader) {
    if (!this.updateCurrentJoh(joh)) return;
    const filter = this.areAllInvoicesPosted(joh)
      ? (invh: InvoiceHeader) => invh.isPostedOrQueued
      : (invh: InvoiceHeader) => !invh.isPostedOrQueued;
    const invh = _.find(joh.invoiceHeaders, filter);
    if (invh) {
      this.openInvoiceDialog(invh);
    }
  }

  updateCurrentJoh(joh: JoHeader) {
    if (joh == null) {
      return false;
    }
    if (joh !== this.currentJoHeader) {
      this.currentJoHeader = joh;
      this.replaceLocation();
    }
    return true;
  }

  hasWIP(joh: JoHeader) {
    // need to determine if there are any itemDetail associated with this invoice
    const jods = joh && joh.joDetails;
    if (jods == null) {
      return false;
    }
    const itds = _.flatMap(jods, (jod) => jod.itemDetails);
    const ok = itds.some(
      (itd) => itd.itemDetailStatusId > ItemDetailStatusEnum.InInventory && itd.itemDetailStatusId < ItemDetailStatusEnum.Shipped,
    );
    return ok;
  }

  // Removed because of new Return Request menu item - RR's are no longer created in this screen

  // canAddEditReturnRequest(joh: JoHeader) {
  //   return (
  //     joh && joh.joStatusId != JoStatusCode.OpenProcessIncomplete && joh.invoiceHeaders?.filter((invh) => invh.isPostedOrQueued).length > 0
  //   );
  // }

  // async addEditReturnRequest(joh: JoHeader) {
  //   AgFns.busyGrid(this.johGridOptions, this, async () => {
  //     await this.queryReturnRequestDetails(joh);

  //     this.router.navigate(["/jo-return-req-auth"], {
  //       queryParams: { joHeaderId: joh.id.toString() },
  //     });
  //   });
  // }

  hasAddonDocs(jodAddon: JoDetailAddon) {
    return jodAddon?.addon.addonDocMaps.length > 0;
  }

  async viewAddonDocs(jodAddon: JoDetailAddon) {
    // const files = jode.addon.addonDocs.map(d => {
    //   const byteArray = stringToByteArray(d.doc);
    //   const file = new File([byteArray], d.fileName, { type: d.mimeType });
    //   return file;
    // });

    const data: FileViewDialogParams = {
      header: 'View Addon Document',
      docs: jodAddon.addon.addonDocMaps.map((x) => x.addonDoc as IDoc),
    };
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
  }

  async onTabChanged(event: any) {
    // BUG FIX: The old code used numeric indices from JoTabEnum to identify tabs.
    // However, some tabs are conditionally rendered with *ngIf (WIP tab only shows in
    // OpenProcessComplete/All modes). When tabs are hidden, the actual rendered indices
    // shift - e.g., in Closed mode, Invoices tab is at index 5, not 6.
    // Using tab header names from the event ensures correct identification regardless
    // of which tabs are currently visible.
    const tabHeader = event.originalEvent?.target?.innerText?.trim();

    // OLD CODE (broken when tabs are conditionally hidden):
    // const tabIx = event.index;
    // if (tabIx == JoTabEnum.Details) { ... }
    // else if (tabIx == JoTabEnum.WIP) { ... }
    // else if (tabIx == JoTabEnum.Invoices) { ... }  // Would never match in Closed mode!

    // hack: because of ag-grid issues with button redraw on ngIf'd tab.
    if (tabHeader === 'Details') {
      // no need to query they are always available
      this.joDetails = this.joDetails.slice(0);
    } else if (tabHeader === 'Purchase Orders') {
      // this.poDetails = this.poDetails.slice(0);
      AgFns.sizeColumnsToFit(this.podGridOptions);
    } else if (tabHeader === 'WIP - Pulled/Received') {
      await this.queryItemDetails(this.currentJoHeader);
      AgFns.sizeColumnsToFit(this.itdGridOptions);
    } else if (tabHeader === 'Invoices') {
      await this.queryInvoices(this.currentJoHeader);
      AgFns.autoSizeAllColumns(this.invhGridOptions);
    } else if (tabHeader === 'Returns') {
      await this.queryReturnRequestDetails(this.currentJoHeader);
      AgFns.sizeColumnsToFit(this.rradGridOptions);
    }
    this.currentTabIx = event.index;
    this.currentTabName = tabHeader;
  }

  async goJoInProcess(joh: JoHeader) {
    if (this.updateCurrentJoh(joh)) {
      const params = NavFns.buildNavIdParams(joh.id, 'id');
      this.router.navigate(['/in-process/manage/jo-in-process'], params);
    }
  }

  async onJohCellValueChanged(event) {
    // if (event != null) {
    //   if (event.oldValue != null || event.newValue != null) {
    //     this.currentProductType.eOQModTs = new Date(Date.now());
    //     this.ptGridOptions.apiX.redrawRows();
    //   }
    // }

    if (event?.column?.colId == 'shipFrequencyId') {
      // if (event.newValue != EOQTypeCode.None) {
      //   // await this.getProductSummaries(this.currentProductType.id);
      //   // this.prodGridOptions.apiX.redrawRows();
      //   // AgFns.autoSizeAllColumns(this.prodGridOptions);
      // }
    }

    // this.prodGridOptions.apiX.stopEditing();
  }

  goReturnRequest() {
    this.router.navigate(['/returns-inbox']);
  }

  goShipping() {
    this.router.navigate(['/jo-multi-ship']);
  }

  goManifests() {
    this.router.navigate(['/manifests']);
  }
}
